import {
  Button,
  H4,
  Icon,
  LabelledCheckbox,
  Stack,
  TextArea,
  Title,
  useActions,
  useTheme,
  useToast
} from "@introist/react-foundation/v2";
import { Info, UpsertFormProps, UpsertModal } from "../../../../foundation";
import { useState } from "react";
import { api } from "../../../../services/rpc/RpcProvider";
import { useIdentity } from "../../../../IdentityProvider";
import moment from "moment";

type DwdConfig = {
  sa?: string;
  scopes: string[];
};

export const GoogleDWDConfig = () => {
  const { theme } = useTheme();

  const { data: config, refetch } = api.workspaces.googledwd.find.useQuery({});

  const create = api.workspaces.googledwd.create.useMutation();
  const update = api.workspaces.googledwd.update.useMutation();
  const remove = api.workspaces.googledwd.delete.useMutation();

  const [open, setOpen] = useState(false);

  return (
    <Stack vertical>
      <H4>Domain-wide delegation</H4>
      {config === null && (
        <Stack>
          <Title>Domain-wide delegation is not in use.</Title>
          <Button variant="outlined" size="small" onClick={() => setOpen(true)}>
            Set up
          </Button>
        </Stack>
      )}
      {!!config && (
        <Stack>
          <Icon
            name="checkCircle"
            color={theme.palette.success.default}
            style={{ width: "14px" }}
          />
          <Title>Domain-wide delegation is configured.</Title>
          <Button variant="outlined" size="small" onClick={() => setOpen(true)}>
            Edit
          </Button>
        </Stack>
      )}
      <UpsertModal
        open={open}
        template={{ scopes: ["calendar"] }}
        onCreate={e =>
          create.mutateAsync({ sa: e.sa!, scopes: e.scopes! }).then(async () => await refetch())
        }
        onUpdate={e => update.mutateAsync(e).then(async () => await refetch())}
        onDelete={async () => remove.mutateAsync({}).then(async () => await refetch())}
        onClose={() => setOpen(false)}
        Form={DwdForm}
        entryName="Domain-wide delegation"
        entry={config ?? undefined}
      />
    </Stack>
  );
};

const DwdForm = ({ value, onChange, isEdit }: UpsertFormProps<DwdConfig>) => {
  const toast = useToast();
  const { onConfirmAction } = useActions();
  const { user } = useIdentity();

  const { sa, scopes } = value;

  const test = api.workspaces.googledwd.test.useMutation();

  const onTestCalendar = onConfirmAction(
    async () => {
      await test
        .mutateAsync({ scope: "calendar", sa: sa && sa.length > 0 ? sa : undefined })
        .then(() => toast.success("Calendar test event created successfully"))
        .catch(() => toast.error("Failed to create calendar test event"));
    },
    {
      title: "Run calendar test",
      description: `This will create a test event to calendar ${user!.email} at ${moment()
        .startOf("hour")
        .add(1, "hour")
        .format("HH:mm")} today.`,
      confirmTitle: "Run test"
    }
  );

  return (
    <Stack vertical style={{ width: "400px" }}>
      <Title>
        Domain-wide delegation enables Introist to act on behalf of any user in your Google
        Workspace domain.
      </Title>
      <TextArea
        placeholder={
          isEdit
            ? "Current service account not shown. Copy new JSON here to update. "
            : "Copy Service account JSON here."
        }
        rows={7}
        value={sa ?? ""}
        onChange={sa => onChange({ ...value, sa })}
      />
      <Title>Enabled for:</Title>
      <Stack>
        <LabelledCheckbox
          label="Calendar"
          checked={scopes.includes("calendar")}
          onChange={checked => {
            if (checked) {
              onChange({ ...value, scopes: [...scopes, "calendar"] });
            } else {
              onChange({ ...value, scopes: scopes.filter((s: string) => s !== "calendar") });
            }
          }}
        />
        <Info message="Requires scope: https://www.googleapis.com/auth/emails.send" />
        <Button variant="blended" size="small" onClick={onTestCalendar}>
          Test
        </Button>
      </Stack>
    </Stack>
  );
};
